import { setDefaultOptions } from 'date-fns';
import { createPinia } from 'pinia';
import { createApp } from 'vue';

import {
  i18n,
  loadLanguageAsync,
} from '@apps/main/modules/localization/localizationSetup';
import router from '@apps/main/modules/router/routerSetup';

import {
  initializeStatsig,
  trackPerformanceLegacy,
  trackPerformance,
  getExperiment,
  assignExperimentValue,
  assignMultiExperimentValue,
  markUnloadedExperimentsAsControl,
} from '@libs/feature-toggle';
import { getLocale } from '@libs/sembo-dates';
import { decodeHTML, getDomain } from '@libs/utils';

import App from './App.vue';
import { createLogger } from './modules/plugins/logger';
import './styles/main.scss';
import { logException } from './modules/plugins/logException';
import { registerPreloadErrorHandler } from './modules/plugins/preloadErrorHandler';

registerPreloadErrorHandler();

const languageLocale = getDomain.getLanguageLocale();

if (decodeHTML(location.href) !== location.href) {
  location.href = decodeHTML(location.href) ?? location.href;
}

trackPerformance();
trackPerformanceLegacy();

(async function (): Promise<void> {
  await initializeStatsig();

  const shouldDimTransportUpsell = getExperiment(
    'hotel_only_dim_transport_upsell'
  ).get('should_dim_transport_upsell', false);
  assignExperimentValue(
    'hotel_only_dim_transport_upsell',
    shouldDimTransportUpsell
  );

  const multiDestinationDimTransportUpsellVariant = getExperiment(
    'multi_destination_dim_transport_upsell'
  ).get('dim_transport_upsell_variant', 'control');
  assignMultiExperimentValue(
    'multi_destination_dim_transport_upsell',
    multiDestinationDimTransportUpsellVariant
  );

  const shouldShowAlternativeCard = getExperiment(
    'hotel_selection_alternative_card_desktop'
  ).get('should_show_alternative_card', false);
  assignExperimentValue(
    'hotel_selection_alternative_card_desktop',
    shouldShowAlternativeCard
  );

  const hotelSelectionMobileCardVariant = getExperiment(
    'hotel_selection_alternative_card_mobile'
  ).get('card_layout_variant', 'control');
  assignMultiExperimentValue(
    'hotel_selection_alternative_card_mobile',
    hotelSelectionMobileCardVariant
  );
})().catch(() => {
  logException('Statsig experiment initialization failed');
  // Unblock consumers awaiting isExperimentAsync so they fall back to control
  // instead of hanging on a promise that never resolves.
  markUnloadedExperimentsAsControl();
});

const app = createApp(App);

const pinia = createPinia();
app.use(pinia);

const logger = createLogger();
app.use(logger);

app.use(router);
app.use(i18n);

app.mount('#app');

// NOTE: This loads sembo shared web components after app is mounted,
// to not delay first request to smart search.
window.addEventListener('load', () => {
  const scripts = [
    'https://sembo-inspire-apis.sembo.travel/share-component/latest/main.js',
    'https://sembo-inspire-apis.sembo.travel/sembo-auth/latest/sembo-auth.js',
    'https://sembo-inspire-apis.sembo.travel/sembo-footer/latest/sembo-footer.js',
  ];

  scripts.forEach((src) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    document.body.appendChild(script);
  });
});

setDefaultOptions({
  locale: getLocale(languageLocale),
});

loadLanguageAsync(languageLocale).catch(() =>
  logException('Internationalization language module loading failed')
);
